import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Layout from "../layout";
import { ROOT_API } from "../../api/config";
import { updateUserInformation } from "../../stores/users/userSlice";
import { formatDate } from "../../utils/dates";

const FEEDBACK_CHOICES = [
  { id: "direct", labelId: "feedback_choice_direct" },
  { id: "partial", labelId: "feedback_choice_partial" },
  { id: "none", labelId: "feedback_choice_none" },
];

const Feedback = () => {
  const { uuid } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState("");
  const [context, setContext] = useState(null);

  const [choice, setChoice] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${ROOT_API}apis/feedback/${uuid}`);
        const payload = res && res.data ? res.data : null;
        const data = payload && payload.data ? payload.data : null;
        if (cancelled) return;
        if (data && data.valid === true) {
          if (data.access_token) {
            localStorage.setItem("access_token", data.access_token);
          }
          if (data.user_data) {
            dispatch(updateUserInformation(data.user_data));
          }
          setContext(data.context || null);
          setIsValid(true);
        } else {
          setInvalidMessage(
            (payload && payload.message) ||
              intl.formatMessage({ id: "feedback_invalid" })
          );
          setIsValid(false);
        }
      } catch (error) {
        if (cancelled) return;
        setInvalidMessage(intl.formatMessage({ id: "feedback_invalid" }));
        setIsValid(false);
      }
      if (!cancelled) setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const handleSubmit = async () => {
    if (!choice) {
      Swal.fire({
        icon: "warning",
        title: intl.formatMessage({ id: "feedback_select_choice" }),
        confirmButtonText: "OK",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${ROOT_API}apis/feedback/${uuid}`, {
        feedback: choice,
        comment: comment || undefined,
      });
      const data = res && res.data && res.data.data ? res.data.data : null;
      if (data && data.ok) {
        setIsDone(true);
      } else {
        setInvalidMessage(
          (res && res.data && res.data.message) ||
            intl.formatMessage({ id: "feedback_invalid" })
        );
        setIsValid(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    }
    setIsSubmitting(false);
  };

  const renderCentered = (children) => (
    <Layout>
      <div className="general-form">
        <div className="container">
          <div className="general-form-main">
            <div className="general-form-main-form p-36">{children}</div>
          </div>
        </div>
      </div>
    </Layout>
  );

  if (isLoading) {
    return renderCentered(
      <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isValid) {
    return renderCentered(
      <p className="font-16 text-center" style={{ lineHeight: 1.6 }}>
        {invalidMessage || intl.formatMessage({ id: "feedback_invalid" })}
      </p>
    );
  }

  if (isDone) {
    return renderCentered(
      <p className="font-bold font-20 text-center" style={{ lineHeight: 1.6 }}>
        {intl.formatMessage({ id: "feedback_thanks" })}
      </p>
    );
  }

  const outages = context && Array.isArray(context.outages) ? context.outages : [];

  return renderCentered(
    <>
      <p className="font-bold font-20 text-center mb-22">
        {intl.formatMessage({ id: "feedback_title" })}
      </p>

      {context && context.address && (
        <p className="font-16 mb-12">
          <strong>{intl.formatMessage({ id: "address" })}:</strong>{" "}
          {context.address}
        </p>
      )}

      {outages.length > 0 && (
        <Box sx={{ marginBottom: "16px" }}>
          <p className="font-16 mb-12">
            <strong>{intl.formatMessage({ id: "outage_information" })}:</strong>
          </p>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {outages.map((o, i) => (
              <li key={i} className="font-14" style={{ marginBottom: "6px" }}>
                {[o.address, formatDate(o.date), o.duration].filter(Boolean).join(" — ")}
              </li>
            ))}
          </ul>
        </Box>
      )}

      <p className="font-16 mb-12">
        {intl.formatMessage({ id: "feedback_question" })}
      </p>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        {FEEDBACK_CHOICES.map((item) => (
          <Button
            key={item.id}
            type="button"
            variant={choice === item.id ? "contained" : "outlined"}
            onClick={() => setChoice(item.id)}
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            {intl.formatMessage({ id: item.labelId })}
          </Button>
        ))}
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label={intl.formatMessage({ id: "feedback_comment" })}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        sx={{ marginBottom: "16px" }}
      />

      <Box sx={{ display: isSubmitting ? "flex" : "none", justifyContent: "center", marginBottom: "8px" }}>
        <CircularProgress />
      </Box>

      <button
        onClick={handleSubmit}
        type="button"
        disabled={isSubmitting}
        className="btn-cta general-form-main-form_submit mt-12"
      >
        {intl.formatMessage({ id: "submit" })}
      </button>
    </>
  );
};

export default Feedback;
